/**
 * useContractConfidenceScore
 *
 * Derives a Confidence Score (0–100) for the fiduciary module.
 * 6 dimensions, 100% read-only, never persisted.
 *
 * D1 — Integridade Financeira (22%)
 * D2 — Aderência Temporal / SPI (18%)
 * D3 — Consistência de Medições (18%)
 * D4 — Qualidade de Evidências (17%)
 * D5 — Verificação Criptográfica (15%)
 * D6 — Coerência Físico × Financeiro (10%)
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// ── Types ────────────────────────────────────────────────────────────────────

export type ConfidenceGrade = 'A' | 'B' | 'C' | 'D';
export type MaturityLevel = 'immature' | 'preliminary' | 'full';

export interface DimensionResult {
  /** Raw score 0–100 (before weight) */
  raw: number;
  /** Weighted contribution to final score */
  weighted: number;
  /** Weight applied (0–1) */
  weight: number;
  /** Human-readable explanation in Portuguese */
  label: string;
  /** Explanation text for this dimension */
  explanation: string;
}

export interface ConfidenceScoreResult {
  score: number;
  grade: ConfidenceGrade;
  maturity: MaturityLevel;
  maturityLabel: string;
  dimensions: {
    integridade: DimensionResult;
    aderencia: DimensionResult;
    medicoes: DimensionResult;
    evidencias: DimensionResult;
    criptografia: DimensionResult;
    coerencia: DimensionResult;
  };
  boletinsAprovados: number;
  isLoading: boolean;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function gradeFrom(score: number): ConfidenceGrade {
  if (score >= 85) return 'A';
  if (score >= 65) return 'B';
  if (score >= 40) return 'C';
  return 'D';
}

function maturityFrom(boletins: number): MaturityLevel {
  if (boletins >= 2) return 'full';
  if (boletins === 1) return 'preliminary';
  return 'immature';
}

function maturityLabel(m: MaturityLevel): string {
  switch (m) {
    case 'immature': return 'Aguardando primeira medição aprovada';
    case 'preliminary': return 'Score preliminar — baseado em 1 medição';
    case 'full': return '';
  }
}

// ── Dimension calculators ────────────────────────────────────────────────────

function calcD1(integridade: any): { raw: number; explanation: string } {
  if (!integridade) return { raw: 0, explanation: 'Dados de integridade financeira não disponíveis' };

  const status = integridade.status_integridade;
  if (status === 'OK') return { raw: 100, explanation: 'Triângulo financeiro consistente (Contrato = PPU = BAC)' };

  const deltaContPpu = Math.abs(Number(integridade.delta_contrato_ppu) || 0);
  const deltaPpuBac = Math.abs(Number(integridade.delta_ppu_bac) || 0);
  const valorGlobal = Math.abs(Number(integridade.valor_global) || 1);

  const pctContPpu = deltaContPpu / valorGlobal;
  const pctPpuBac = deltaPpuBac / valorGlobal;

  const scorePpu = pctContPpu <= 0.01 ? 50 : pctContPpu <= 0.05 ? 25 : 0;
  const scoreBac = pctPpuBac <= 0.01 ? 50 : pctPpuBac <= 0.05 ? 25 : 0;
  const raw = scorePpu + scoreBac;

  if (raw >= 80) return { raw, explanation: 'Divergência marginal entre contrato e orçamento' };
  if (raw >= 40) return { raw, explanation: 'Divergência moderada — verificar planilha e cronograma' };
  return { raw, explanation: 'Divergência significativa no triângulo financeiro' };
}

function calcD2(spiRecords: any[]): { raw: number; explanation: string } {
  if (!spiRecords || spiRecords.length === 0) {
    return { raw: 50, explanation: 'Sem dados de SPI disponíveis' };
  }

  const latest = Number(spiRecords[0]?.spi);
  if (isNaN(latest)) return { raw: 50, explanation: 'SPI não calculado para este período' };

  let raw: number;
  if (latest >= 1.00) raw = 100;
  else if (latest >= 0.95) raw = 90;
  else if (latest >= 0.85) raw = 70;
  else if (latest >= 0.70) raw = 45;
  else raw = 15;

  if (spiRecords.length >= 3) {
    const vals = spiRecords.slice(0, 3).map(r => Number(r.spi)).reverse();
    const trend = vals[2] - vals[0];
    if (trend > 0.05) raw = Math.min(100, raw + 10);
    else if (trend < -0.05) raw = Math.max(0, raw - 10);
  }

  const spiStr = latest.toFixed(2);
  if (raw >= 90) return { raw, explanation: `SPI ${spiStr} — execução no ritmo planejado` };
  if (raw >= 70) return { raw, explanation: `SPI ${spiStr} — desvio leve de cronograma` };
  if (raw >= 45) return { raw, explanation: `SPI ${spiStr} — atraso significativo na execução` };
  return { raw, explanation: `SPI ${spiStr} — atraso crítico no cronograma` };
}

function calcD3(boletins: any[], glosas: any[]): { raw: number; explanation: string } {
  const aprovados = boletins.filter(b => b.status === 'aprovado' || b.status === 'aprovado_para_faturamento');
  if (aprovados.length === 0) return { raw: 50, explanation: 'Nenhum boletim aprovado para análise' };

  const totalBruto = aprovados.reduce((s, b) => s + (Number(b.valor_bruto) || 0), 0);
  const totalGlosado = aprovados.reduce((s, b) => s + (Number(b.valor_glosas) || 0), 0);
  const taxaGlosa = totalBruto > 0 ? totalGlosado / totalBruto : 0;

  let pts3a: number;
  if (taxaGlosa === 0) pts3a = 40;
  else if (taxaGlosa < 0.02) pts3a = 35;
  else if (taxaGlosa < 0.05) pts3a = 25;
  else if (taxaGlosa < 0.10) pts3a = 15;
  else pts3a = 0;

  const totalGlosas = glosas.length;
  const glosaPendente = glosas.filter(g => g.status === 'pendente').length;
  const taxaPendencia = totalGlosas > 0 ? glosaPendente / totalGlosas : 0;

  let pts3b: number;
  if (totalGlosas === 0) pts3b = 30;
  else if (taxaPendencia === 0) pts3b = 30;
  else if (taxaPendencia < 0.20) pts3b = 22;
  else if (taxaPendencia < 0.50) pts3b = 12;
  else pts3b = 0;

  const periodos = aprovados
    .map(b => b.periodo_inicio)
    .filter(Boolean)
    .sort();

  let gaps = 0;
  if (periodos.length >= 2) {
    for (let i = 1; i < periodos.length; i++) {
      const prev = new Date(periodos[i - 1]);
      const curr = new Date(periodos[i]);
      const diffMonths = (curr.getFullYear() - prev.getFullYear()) * 12 + (curr.getMonth() - prev.getMonth());
      if (diffMonths > 2) gaps++;
    }
  }

  let pts3c: number;
  if (gaps === 0) pts3c = 30;
  else if (gaps === 1) pts3c = 20;
  else if (gaps === 2) pts3c = 10;
  else pts3c = 0;

  const raw = Math.round((pts3a + pts3b + pts3c));

  if (raw >= 85) return { raw, explanation: `${aprovados.length} medições regulares sem glosas relevantes` };
  if (raw >= 60) return { raw, explanation: `${glosaPendente > 0 ? glosaPendente + ' glosa(s) pendente(s)' : 'Medições com pequenas inconsistências'}` };
  if (raw >= 30) return { raw, explanation: `Taxa de glosa de ${(taxaGlosa * 100).toFixed(1)}% — atenção necessária` };
  return { raw, explanation: 'Inconsistências significativas nas medições' };
}

function calcD4(execucoes: any[], evidencias: any[]): { raw: number; explanation: string } {
  if (execucoes.length === 0) return { raw: 50, explanation: 'Sem execuções validadas para análise' };

  const total = execucoes.length;

  const idsComEvidencia = new Set(evidencias.map(e => e.execucao_id));
  const comEvidencia = execucoes.filter(e => idsComEvidencia.has(e.id)).length;
  const taxaEvid = comEvidencia / total;

  let pts4a: number;
  if (taxaEvid >= 0.95) pts4a = 40;
  else if (taxaEvid >= 0.80) pts4a = 30;
  else if (taxaEvid >= 0.60) pts4a = 18;
  else pts4a = 5;

  const qgAprovadas = execucoes.filter(e => e.qualidade_status === 'aprovada').length;
  const taxaQg = qgAprovadas / total;

  let pts4b: number;
  if (taxaQg >= 0.90) pts4b = 35;
  else if (taxaQg >= 0.70) pts4b = 25;
  else if (taxaQg >= 0.50) pts4b = 15;
  else pts4b = 0;

  const comGps = evidencias.filter(e => e.exif_latitude != null).length;
  const totalEvid = evidencias.length;
  const taxaGps = totalEvid > 0 ? comGps / totalEvid : 0;

  let pts4c: number;
  if (taxaGps >= 0.80) pts4c = 25;
  else if (taxaGps >= 0.50) pts4c = 18;
  else if (taxaGps >= 0.20) pts4c = 10;
  else pts4c = 3;

  const raw = pts4a + pts4b + pts4c;

  const evidPct = (taxaEvid * 100).toFixed(0);
  if (raw >= 80) return { raw, explanation: `${evidPct}% das execuções com evidência fotográfica verificada` };
  if (raw >= 50) return { raw, explanation: `${evidPct}% das execuções com evidência — cobertura parcial` };
  return { raw, explanation: `Apenas ${evidPct}% das execuções possuem evidência fotográfica` };
}

/**
 * D5 — Verificação Criptográfica (15%)
 *
 * Fórmula endurecida:
 *   base = (fechados_validos / total_ativos) * 100
 *   penalidade_gaps = -15 por gap temporal (períodos faltantes entre boletins)
 *   penalidade_superseded = -5 por registro reconstruído no contrato
 *   penalidade_abertos = -10 se há registros abertos
 *   bonus_continuidade = +10 se cadeia 100% contínua (GENESIS→último sem quebra)
 *
 *   D5 = clamp(base + bonus - penalidades, 0, 100)
 *
 * Peso: continuidade da cadeia > volume puro de registros
 */
function calcD5(
  ledgerRecords: any[],
  boletinsAprovados: number,
  supersededCount: number
): { raw: number; explanation: string } {
  // No ledger at all → neutral fallback
  if (!ledgerRecords || ledgerRecords.length === 0) {
    return { raw: 50, explanation: 'Aguardando ativação do selo criptográfico' };
  }

  const active = ledgerRecords.filter((r: any) => r.status !== 'superseded');
  const total = active.length;
  if (total === 0) {
    return { raw: 50, explanation: 'Aguardando ativação do selo criptográfico' };
  }

  const fechadosValidos = active.filter(
    (r: any) => r.status === 'fechado' && r.hash_atual && r.hash_atual.length === 64
  ).length;

  const abertos = active.filter((r: any) => r.status === 'aberto').length;

  // ── Chain continuity check ──
  const sorted = [...active].sort((a: any, b: any) => a.sequencia - b.sequencia);
  let chainBroken = false;
  if (sorted.length > 0 && sorted[0].hash_anterior !== 'GENESIS') {
    chainBroken = true;
  }
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].hash_anterior !== sorted[i - 1].hash_atual) {
      chainBroken = true;
      break;
    }
  }

  if (chainBroken) {
    return { raw: 15, explanation: 'Cadeia criptográfica com inconsistência detectada' };
  }

  // ── Temporal gap detection ──
  const periodos = sorted
    .map((r: any) => r.periodo_competencia)
    .filter(Boolean)
    .sort();

  let temporalGaps = 0;
  for (let i = 1; i < periodos.length; i++) {
    const prev = new Date(periodos[i - 1]);
    const curr = new Date(periodos[i]);
    const diffMonths = (curr.getFullYear() - prev.getFullYear()) * 12 + (curr.getMonth() - prev.getMonth());
    if (diffMonths > 2) temporalGaps++;
  }

  // ── Coverage: ledger vs approved boletins ──
  const coverageRatio = boletinsAprovados > 0 ? Math.min(total / boletinsAprovados, 1) : 1;

  // ── Score calculation ──
  const base = Math.round((fechadosValidos / total) * 100);

  // Penalties
  const penGaps = temporalGaps * 15;
  const penSuperseded = Math.min(supersededCount * 5, 20);
  const penAbertos = abertos > 0 ? 10 : 0;
  const penCoverage = coverageRatio < 1 ? Math.round((1 - coverageRatio) * 30) : 0;

  // Bonus for perfect continuity
  const bonusContinuidade = (fechadosValidos === total && temporalGaps === 0 && abertos === 0) ? 10 : 0;

  const raw = Math.max(0, Math.min(100, base - penGaps - penSuperseded - penAbertos - penCoverage + bonusContinuidade));

  // Explanations
  if (raw === 100) {
    return { raw, explanation: `${total} períodos verificados — cadeia íntegra e contínua` };
  }
  if (raw >= 85) {
    return { raw, explanation: `${fechadosValidos} de ${total} períodos com selo válido — cadeia consistente` };
  }

  const issues: string[] = [];
  if (temporalGaps > 0) issues.push(`${temporalGaps} gap(s) temporal(is)`);
  if (abertos > 0) issues.push(`${abertos} período(s) aberto(s)`);
  if (supersededCount > 0) issues.push(`${supersededCount} reconstrução(ões)`);
  if (coverageRatio < 1) issues.push(`cobertura ${(coverageRatio * 100).toFixed(0)}%`);

  const issueStr = issues.length > 0 ? ` — ${issues.join(', ')}` : '';

  if (raw >= 60) {
    return { raw, explanation: `${fechadosValidos} de ${total} períodos verificados${issueStr}` };
  }
  if (raw >= 35) {
    return { raw, explanation: `Cobertura parcial do ledger${issueStr}` };
  }
  return { raw, explanation: `Verificação criptográfica insuficiente${issueStr}` };
}

function calcD6(boletins: any[], progresso: any): { raw: number; explanation: string } {
  const aprovados = boletins
    .filter(b => b.status === 'aprovado' || b.status === 'aprovado_para_faturamento')
    .sort((a, b) => (b.periodo_fim || '').localeCompare(a.periodo_fim || ''));

  let fisico: number | null = null;
  let financeiro: number | null = null;

  if (aprovados.length > 0) {
    fisico = aprovados[0].percentual_fisico_acumulado != null ? Number(aprovados[0].percentual_fisico_acumulado) : null;
    financeiro = aprovados[0].percentual_financeiro_acumulado != null ? Number(aprovados[0].percentual_financeiro_acumulado) : null;
  }

  if (fisico == null && progresso) {
    fisico = Number(progresso.progresso_fisico_pct) || null;
  }

  if (fisico == null || financeiro == null) {
    return { raw: 50, explanation: 'Dados insuficientes para análise de coerência' };
  }

  const delta = Math.abs(fisico - financeiro);

  let raw: number;
  if (delta <= 3) raw = 100;
  else if (delta <= 8) raw = 70;
  else if (delta <= 15) raw = 35;
  else raw = 0;

  const deltaStr = delta.toFixed(1);
  if (raw >= 70) return { raw, explanation: `Desvio de ${deltaStr}pp entre progresso físico e financeiro` };
  if (raw >= 35) return { raw, explanation: `Desvio de ${deltaStr}pp — desalinhamento moderado` };
  return { raw, explanation: `Desvio de ${deltaStr}pp — desalinhamento significativo` };
}

// ── Weights ──────────────────────────────────────────────────────────────────

const WEIGHTS = {
  integridade: 0.22,
  aderencia: 0.18,
  medicoes: 0.18,
  evidencias: 0.17,
  criptografia: 0.15,
  coerencia: 0.10,
} as const;

// ── Main hook ────────────────────────────────────────────────────────────────

export function useContractConfidenceScore(contratoId: string): ConfidenceScoreResult {
  const { data, isLoading } = useQuery({
    queryKey: ['contract-confidence-score', contratoId],
    queryFn: async () => {
      const [integridadeRes, spiRes, boletinsRes, glosasRes, execucoesRes, evidenciasRes, progressoRes, ledgerRes, supersededRes] = await Promise.all([
        (supabase as any)
          .from('emp_vw_integridade_financeira')
          .select('*')
          .eq('contrato_id', contratoId)
          .maybeSingle(),
        (supabase as any)
          .from('emp_vw_indicadores_blindados')
          .select('spi, periodo_referencia')
          .eq('contrato_id', contratoId)
          .not('spi', 'is', null)
          .order('periodo_referencia', { ascending: false })
          .limit(3),
        (supabase as any)
          .from('emp_boletins_medicao')
          .select('id, status, valor_bruto, valor_glosas, periodo_inicio, periodo_fim, percentual_fisico_acumulado, percentual_financeiro_acumulado')
          .eq('contrato_id', contratoId),
        (supabase as any)
          .from('emp_glosas')
          .select('id, status')
          .eq('contrato_id', contratoId),
        (supabase as any)
          .from('emp_validacoes_execucao')
          .select('id, qualidade_status')
          .eq('contrato_id', contratoId)
          .in('status', ['disponivel_para_medicao', 'vinculada_a_boletim']),
        (supabase as any)
          .from('emp_evidencias')
          .select('execucao_id, exif_latitude')
          .eq('contrato_id', contratoId)
          .eq('excluido', false),
        (supabase as any)
          .from('emp_vw_progresso_fisico_contrato')
          .select('progresso_fisico_pct')
          .eq('contrato_id', contratoId)
          .maybeSingle(),
        (supabase as any)
          .from('emp_reconciliation_ledger')
          .select('sequencia, periodo_competencia, hash_atual, hash_anterior, status')
          .eq('contrato_id', contratoId)
          .neq('status', 'superseded')
          .order('sequencia', { ascending: true }),
        // Count superseded records for penalty
        (supabase as any)
          .from('emp_reconciliation_ledger')
          .select('id', { count: 'exact', head: true })
          .eq('contrato_id', contratoId)
          .eq('status', 'superseded'),
      ]);

      const boletins = boletinsRes.data ?? [];
      const glosas = glosasRes.data ?? [];
      const execucoes = execucoesRes.data ?? [];
      const evidencias = evidenciasRes.data ?? [];
      const ledgerRecords = ledgerRes.data ?? [];
      const supersededCount = supersededRes.count ?? 0;

      const boletinsAprovados = boletins.filter(
        (b: any) => b.status === 'aprovado' || b.status === 'aprovado_para_faturamento'
      ).length;

      const d1 = calcD1(integridadeRes.data);
      const d2 = calcD2(spiRes.data ?? []);
      const d3 = calcD3(boletins, glosas);
      const d4 = calcD4(execucoes, evidencias);
      const d5 = calcD5(ledgerRecords, boletinsAprovados, supersededCount);
      const d6 = calcD6(boletins, progressoRes.data);

      const score = Math.round(
        d1.raw * WEIGHTS.integridade +
        d2.raw * WEIGHTS.aderencia +
        d3.raw * WEIGHTS.medicoes +
        d4.raw * WEIGHTS.evidencias +
        d5.raw * WEIGHTS.criptografia +
        d6.raw * WEIGHTS.coerencia
      );

      const maturity = maturityFrom(boletinsAprovados);

      return {
        score,
        grade: gradeFrom(score),
        maturity,
        maturityLabel: maturityLabel(maturity),
        boletinsAprovados,
        dimensions: {
          integridade: { raw: d1.raw, weighted: Math.round(d1.raw * WEIGHTS.integridade), weight: WEIGHTS.integridade, label: 'Integridade Financeira', explanation: d1.explanation },
          aderencia: { raw: d2.raw, weighted: Math.round(d2.raw * WEIGHTS.aderencia), weight: WEIGHTS.aderencia, label: 'Aderência ao Cronograma', explanation: d2.explanation },
          medicoes: { raw: d3.raw, weighted: Math.round(d3.raw * WEIGHTS.medicoes), weight: WEIGHTS.medicoes, label: 'Consistência de Medições', explanation: d3.explanation },
          evidencias: { raw: d4.raw, weighted: Math.round(d4.raw * WEIGHTS.evidencias), weight: WEIGHTS.evidencias, label: 'Qualidade de Evidências', explanation: d4.explanation },
          criptografia: { raw: d5.raw, weighted: Math.round(d5.raw * WEIGHTS.criptografia), weight: WEIGHTS.criptografia, label: 'Verificação Criptográfica', explanation: d5.explanation },
          coerencia: { raw: d6.raw, weighted: Math.round(d6.raw * WEIGHTS.coerencia), weight: WEIGHTS.coerencia, label: 'Coerência Físico-Financeira', explanation: d6.explanation },
        },
      };
    },
    enabled: !!contratoId,
    staleTime: 5 * 60_000,
  });

  if (!data) {
    return {
      score: 0,
      grade: 'D',
      maturity: 'immature',
      maturityLabel: maturityLabel('immature'),
      boletinsAprovados: 0,
      dimensions: {
        integridade: { raw: 0, weighted: 0, weight: WEIGHTS.integridade, label: 'Integridade Financeira', explanation: 'Carregando...' },
        aderencia: { raw: 0, weighted: 0, weight: WEIGHTS.aderencia, label: 'Aderência ao Cronograma', explanation: 'Carregando...' },
        medicoes: { raw: 0, weighted: 0, weight: WEIGHTS.medicoes, label: 'Consistência de Medições', explanation: 'Carregando...' },
        evidencias: { raw: 0, weighted: 0, weight: WEIGHTS.evidencias, label: 'Qualidade de Evidências', explanation: 'Carregando...' },
        criptografia: { raw: 0, weighted: 0, weight: WEIGHTS.criptografia, label: 'Verificação Criptográfica', explanation: 'Carregando...' },
        coerencia: { raw: 0, weighted: 0, weight: WEIGHTS.coerencia, label: 'Coerência Físico-Financeira', explanation: 'Carregando...' },
      },
      isLoading,
    };
  }

  return { ...data, isLoading };
}
